import {DataTestAttrs} from '@benchling/js-test-utils';
import {ReactNode} from 'react';

import {ElementKeyboardEvent} from 'util/Types';

export enum TextInputType {
  PASSWORD = 'password',
  TEXT = 'text',
}

export const TEXT_INPUT_TYPES = {
  PASSWORD: 'password',
  TEXT: 'text',
};

export enum LabelStyle {
  HIDDEN = 'hidden',
  LABEL = 'label',
  SUBLABEL = 'sublabel',
}

export const LABEL_STYLES = {
  /**
   * @note If you are running into overflow issues using hidden labels, try adding `position: relative`
   * to the label's parent. Otherwise, the label (which is applied `position: absolute` by the `sr-only`
   * class) gets positioned relative to its nearest relative-positioned ancestor (which is often a buffer tab)
   * and this can lead to buggy overflow behavior on Chrome.
   *
   * We can't easily fix this because some components accidentally rely on this behavior, like the Search
   * DateFilter components.
   *
   * See https://benchling.slack.com/archives/C08ELD8PQ/p1674499138909389 for more context.
   */
  HIDDEN: 'hidden',
  LABEL: 'label',
  SUBLABEL: 'sublabel',
};

export enum Size {
  SMALL = 'small',
  MEDIUM = 'medium',
}

// Other sizes may be added, but small and medium are the only ones currently implemented
export const SIZES = {
  SMALL: 'small',
  MEDIUM: 'medium',
};

// TODO BNCH-48321: Add a few more variants of input error message style.
export enum InputErrorMessageStyle {
  INFO = 'info',
  ERROR = 'error',
}

export interface LabelProps {
  isRequired?: boolean;
  label?: string | JSX.Element;
  labelStyle?: string;
  help?: ReactNode;
}

export interface InputProps extends LabelProps {
  button?: JSX.Element;
  // CSS class applied to the InputWrapper
  className?: string;
  description?: string | JSX.Element;
  descriptionClassName?: string;
  error?: {
    message: ReactNode;
    style: InputErrorMessageStyle;
  } | null;
  // CSS class applied to the actual input
  inputClassName?: string;
  // TODO(TypeScript): Make required once defaultProps are handled correctly by TypeScript.
  size?: Size;
  isDisabled?: boolean;
}

export const DEFAULT_PROPS = {
  isRequired: false,
  labelStyle: LABEL_STYLES.LABEL,
  size: Size.SMALL,
  isDisabled: false,
};

export type DatetimePickerRef = (node: Element | null) => void;

export interface DateAndDatetimeBaseProps extends InputProps {
  autoFocus?: boolean;
  hasButton?: boolean;
  min?: string;
  max?: string;
  onKeyDown?: (e: ElementKeyboardEvent) => void;
  placeholder?: string;
  value?: string | null;
  onChange?: (value: string) => void;
  datePickerPopperContainerSelector?: string;
  wrapperClassName?: string;
  datetimePickerRef?: DatetimePickerRef;
}
export interface DateFieldProps extends DateAndDatetimeBaseProps {
  dataTestAttrs?: DataTestAttrs;
  validDatetimeInputFormats?: Array<string>;
  datetimePresentationalFormat?: string;
  shouldCloseOnSelect?: boolean;
}

export interface DatetimeFieldProps extends DateFieldProps {
  timeInputPlaceholder?: string;
  timeInformalInputFormats?: Array<string>;
  timeFormalInputFormats?: Array<string>;
  timePresentationalFormat?: string;
  preventDefault?: boolean;
}

export interface DateInputProps extends DateFieldProps {
  value: string;
}

export interface DatetimeInputProps extends DatetimeFieldProps {
  timeInputPlaceholder: string;
  timeInformalInputFormats: Array<string>;
  timeFormalInputFormats: Array<string>;
  timePresentationalFormat: string;
  validDatetimeInputFormats: Array<string>;
  datetimePresentationalFormat: string;
  value: string;
}
